import { Link } from "react-router";
import { CalendarDays, Wallet, Images, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * 로그인 전 첫 화면.
 *
 * 예전에는 Clerk 로그인 카드만 덩그러니 떠서, 주소로 처음 들어온 사람은 **여기가
 * 무슨 서비스인지 알 수 없었다.** 이름과 한 줄 설명, 무엇을 할 수 있는지를 먼저
 * 보여주고 **로그인은 버튼을 눌러야** 넘어가게 했다.
 *
 * 로그인 폼을 여기 바로 박지 않은 이유: 처음 온 사람에게 가장 먼저 필요한 건
 * "여기가 뭐 하는 곳인가"이지 이메일 입력칸이 아니다. 이미 쓰던 사람은 버튼을
 * 한 번 더 누를 뿐이고, 그 대가로 첫 방문자가 이탈하지 않는다.
 *
 * 마케팅 페이지를 따로 만들지는 않았다 — 이 앱은 초대로 들어오는 사람이
 * 대부분이고(초대 링크는 `/join/:code`로 따로 있다), 그들에게 필요한 건
 * "여기가 맞나"를 확인할 한 문단이다.
 */
const FEATURES = [
  { icon: CalendarDays, title: "일정", body: "Day별로 이동·숙소·항공편을 모아두고 함께 고쳐요." },
  { icon: Wallet, title: "정산", body: "공금과 대납을 섞어 써도 누가 누구에게 얼마인지 계산해줘요." },
  { icon: Images, title: "기록", body: "사진·일기·GPX 트랙을 남겨 여행기 앨범으로 묶어요." },
  { icon: WifiOff, title: "오프라인", body: "현지에서 인터넷이 없어도 지난 화면을 다시 볼 수 있어요." },
];

export function Landing() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-8 px-5 py-14">
      <div className="flex w-full max-w-md flex-col items-center gap-4 text-center">
        <h1 className="bg-gradient-to-r from-primary to-indigo-500 bg-clip-text text-4xl font-bold tracking-tight text-transparent">
          여행가자
        </h1>
        <p className="text-balance text-lg font-medium text-foreground">
          여행 일정, 비용 정산, 기록을 한 곳에서
        </p>
        <p className="text-balance text-sm leading-relaxed text-muted-foreground">
          동행자와 같은 화면을 보며 계획하고, 쓴 돈은 알아서 정산되고, 다녀온 뒤에는 앨범이 남아요.
        </p>

        <ul className="mt-2 grid w-full gap-3 text-left sm:grid-cols-2">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <li key={title} className="flex flex-col gap-1 rounded-lg border border-border/70 bg-card p-3">
              <span className="flex items-center gap-1.5 text-sm font-semibold">
                <Icon className="size-4 text-primary" aria-hidden />
                {title}
              </span>
              <span className="text-xs leading-relaxed text-muted-foreground">{body}</span>
            </li>
          ))}
        </ul>

        <div className="mt-4 flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
          <Button asChild size="lg" className="sm:min-w-44">
            <Link to="/sign-in">앱 사용하기</Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="sm:min-w-44">
            <Link to="/help">도움말</Link>
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          처음이신가요? <b>앱 사용하기</b>에서 회원가입도 할 수 있어요.
        </p>
      </div>
    </div>
  );
}
